/**
 * Строка активных фильтров, 30 px (§4.1, §10.2, §5.4).
 *
 * Правило спеки: любой включённый фильтр — хоть из чипа, хоть из панели —
 * немедленно становится снимаемой пилюлей. Состояние экрана всегда видно
 * целиком, без открытия панелей. Строка появляется только при активных
 * фильтрах (или при расхождении снимка с фильтром) и исчезает без них.
 */
import { useMemo } from 'react'
import type { Category, EnumValue, UserSummary } from '@shared/types.ts'
import { Button, IconButton, IconClose } from '../../../components/index.js'
import { useApp } from '../../../store/app-store.js'
import {
  FLAG_ON, GROUP_ENUM_SET, SYSTEM_AUTHOR_ID, UNSET,
  type BooleanFilterKey, type BrandFilters, type NumberGroupKey, type ValueGroupKey,
} from './apply.js'
import type { BrandFiltersState } from './useBrandFilters.js'
import { filterTexts } from './texts.filters.js'
import './ActivePills.css'

export interface ActivePillsProps {
  state: BrandFiltersState
  /**
   * Рисовать ли пилюли активных фильтров. На главном экране они выключены:
   * выбранные значения видны прямо в пилюлях полосы фильтров, и две строки с
   * одним и тем же были нагромождением. Сообщение о расхождении снимка
   * (§5.4) показывается в любом случае — его больше показывать негде.
   */
  showPills?: boolean
}

export interface Pill {
  id: string
  /** Название группы: «Тир», «Категория». Для флагов пусто. */
  group?: string
  value: string
  /** Исключающий фильтр: пилюля со знаком «−» и вдавленным фоном (§10.1). */
  excluded?: boolean
  next: BrandFilters
}

const VALUE_GROUPS: ValueGroupKey[] = [
  'tiers', 'visibility', 'seoPriority', 'contextPriority', 'stockDisplay', 'oos',
]

const GROUP_LABEL: Record<ValueGroupKey, string> = {
  tiers: filterTexts.group.tiers,
  visibility: filterTexts.group.visibility,
  seoPriority: filterTexts.group.seoPriority,
  contextPriority: filterTexts.group.contextPriority,
  stockDisplay: filterTexts.group.stockDisplay,
  oos: filterTexts.group.oos,
}

const FLAG_LABEL: Record<BooleanFilterKey, string> = {
  hasUrl: filterTexts.chips.noUrl,
  categoriesUnclear: filterTexts.panel.unclear,
  hasComments: filterTexts.panel.comments,
  hasTask: filterTexts.panel.hasTask,
  channelDiff: filterTexts.panel.channelDiff,
  onlyOneChannel: filterTexts.panel.onlyOneChannel,
  onlyMine: filterTexts.panel.onlyMine,
  lensVisit: filterTexts.chips.lens,
  todo: filterTexts.group.todo,
  myMissing: filterTexts.group.myMissing,
}

const PRESENT_LABEL: Record<string, string> = {
  retail: filterTexts.panel.presentRetail,
  b2b: filterTexts.panel.presentB2b,
  both: filterTexts.panel.presentBoth,
  only_one: filterTexts.panel.presentOnlyOne,
}

const STATUS_LABEL: Record<string, string> = {
  active: filterTexts.panel.statusActive,
  archived: filterTexts.panel.statusArchived,
  all: filterTexts.panel.statusAll,
}

const WG_LABEL: Record<string, string> = {
  delivery: filterTexts.panel.wgDelivery,
  installation: filterTexts.panel.wgInstallation,
  warranty: filterTexts.panel.wgWarranty,
}

/** Подпись значения справочника; «не задано» — текстом, а не пустотой (П3). */
function enumLabel(values: readonly EnumValue[] | undefined, value: string | null, unset: string): string {
  if (value === null || value === UNSET) return unset
  return values?.find((item) => item.value === value)?.label ?? value
}

function withoutGroup(filters: BrandFilters, group: ValueGroupKey | NumberGroupKey): BrandFilters {
  const next = { ...filters }
  delete next[group]
  return next
}

function withoutExclusion(filters: BrandFilters, group: ValueGroupKey | NumberGroupKey): BrandFilters {
  if (!filters.exclude) return filters
  const exclude = { ...filters.exclude }
  delete exclude[group]
  const next = { ...filters }
  if (Object.keys(exclude).length === 0) delete next.exclude
  else next.exclude = exclude
  return next
}

function withoutNumber(
  filters: BrandFilters, group: NumberGroupKey, id: number, excluded: boolean,
): BrandFilters {
  if (!excluded) {
    const list = (filters[group] ?? []).filter((item) => item !== id)
    const next = { ...filters }
    if (list.length === 0) delete next[group]
    else next[group] = list
    return next
  }
  const list = (filters.exclude?.[group] ?? []).filter((item) => item !== id)
  const exclude = { ...filters.exclude }
  if (list.length === 0) delete exclude[group]
  else exclude[group] = list
  const next = { ...filters }
  if (Object.keys(exclude).length === 0) delete next.exclude
  else next.exclude = exclude
  return next
}

/** Описание активных фильтров пилюлями: нужно и строке, и диалогу представления. */
export function buildPills(
  filters: BrandFilters,
  enums: Record<string, EnumValue[]>,
  categories: readonly Category[],
  users: readonly UserSummary[],
  fieldName: (code: string) => string,
): Pill[] {
  const pills: Pill[] = []

  if (filters.query) {
    const next = { ...filters }
    delete next.query
    pills.push({ id: 'query', group: filterTexts.group.query, value: filters.query, next })
  }

  for (const group of VALUE_GROUPS) {
    const set = GROUP_ENUM_SET[group]
    const unset = group === 'tiers' ? filterTexts.panel.unsetTier : filterTexts.panel.unset
    const include = filters[group] ?? []
    if (include.length > 0) {
      pills.push({
        id: `${group}`,
        group: GROUP_LABEL[group],
        value: include.map((value) => enumLabel(enums[set], value, unset)).join(', '),
        next: withoutGroup(filters, group),
      })
    }
    const exclude = filters.exclude?.[group] ?? []
    if (exclude.length > 0) {
      pills.push({
        id: `-${group}`,
        group: GROUP_LABEL[group],
        value: exclude.map((value) => enumLabel(enums[set], value, unset)).join(', '),
        excluded: true,
        next: withoutExclusion(filters, group),
      })
    }
  }

  /* Категории выносятся отдельными пилюлями: их выбирают по одной (§10.2). */
  for (const id of filters.categoryIds ?? []) {
    pills.push({
      id: `cat-${id}`,
      group: filterTexts.group.categoryIds,
      value: categories.find((item) => item.id === id)?.name ?? String(id),
      next: withoutNumber(filters, 'categoryIds', id, false),
    })
  }
  for (const id of filters.exclude?.categoryIds ?? []) {
    pills.push({
      id: `-cat-${id}`,
      group: filterTexts.group.categoryIds,
      value: categories.find((item) => item.id === id)?.name ?? String(id),
      excluded: true,
      next: withoutNumber(filters, 'categoryIds', id, true),
    })
  }

  const authorName = (id: number) =>
    id === SYSTEM_AUTHOR_ID
      ? filterTexts.panel.changedBySystem
      : users.find((item) => item.id === id)?.shortName ?? String(id)

  if ((filters.changedBy ?? []).length > 0) {
    pills.push({
      id: 'by',
      group: filterTexts.group.changedBy,
      value: (filters.changedBy ?? []).map(authorName).join(', '),
      next: withoutGroup(filters, 'changedBy'),
    })
  }
  if ((filters.exclude?.changedBy ?? []).length > 0) {
    pills.push({
      id: '-by',
      group: filterTexts.group.changedBy,
      value: (filters.exclude?.changedBy ?? []).map(authorName).join(', '),
      excluded: true,
      next: withoutExclusion(filters, 'changedBy'),
    })
  }

  if (filters.presentIn) {
    const next = { ...filters }
    delete next.presentIn
    pills.push({
      id: 'pres',
      group: filterTexts.group.presentIn,
      value: PRESENT_LABEL[filters.presentIn] ?? filters.presentIn,
      next,
    })
  }

  if (filters.changedWithinDays !== undefined) {
    const next = { ...filters }
    delete next.changedWithinDays
    pills.push({
      id: 'chg',
      group: filterTexts.group.changedWithinDays,
      value: filterTexts.panel.changedDays(filters.changedWithinDays),
      next,
    })
  }

  if (filters.status && filters.status !== 'active') {
    const next = { ...filters }
    delete next.status
    pills.push({
      id: 'st',
      group: filterTexts.group.status,
      value: STATUS_LABEL[filters.status] ?? filters.status,
      next,
    })
  }

  if ((filters.whiteGlove ?? []).length > 0) {
    const next = { ...filters }
    delete next.whiteGlove
    pills.push({
      id: 'wg',
      group: filterTexts.group.whiteGlove,
      value: (filters.whiteGlove ?? []).map((field) => WG_LABEL[field] ?? field).join(', '),
      next,
    })
  }

  for (const [flag, on] of Object.entries(FLAG_ON) as [BooleanFilterKey, boolean][]) {
    const value = filters[flag]
    if (value === undefined) continue
    const next = { ...filters }
    delete next[flag]
    pills.push({ id: flag, value: FLAG_LABEL[flag], excluded: value !== on, next })
  }

  for (const code of Object.keys(filters.fields ?? {})) {
    const fields = { ...filters.fields }
    delete fields[code]
    const next = { ...filters }
    if (Object.keys(fields).length === 0) delete next.fields
    else next.fields = fields
    pills.push({ id: `f-${code}`, group: fieldName(code), value: filterTexts.panel.any, next })
  }

  return pills
}

export function ActivePills({ state, showPills = true }: ActivePillsProps) {
  const bootstrap = useApp((s) => s.bootstrap)
  const { filters, setFilters } = state

  const pills = useMemo(
    () => buildPills(
      filters,
      bootstrap?.enums ?? {},
      bootstrap?.categories ?? [],
      bootstrap?.users ?? [],
      (code) => bootstrap?.fields.find((field) => field.code === code)?.name ?? code,
    ),
    [filters, bootstrap],
  )

  const stale = state.staleBrandIds.size
  const shown = showPills ? pills : []

  if (shown.length === 0 && stale === 0) return null

  return (
    <div className="active-pills" role="group" aria-label={filterTexts.pills.label}>
      <div className="active-pills__scroll">
        {shown.map((pill) => {
          const name = pill.group ? `${pill.group}: ${pill.value}` : pill.value
          const classes = ['active-pills__pill', 't-meta']
          if (pill.excluded) classes.push('active-pills__pill--excluded')
          return (
            <span key={pill.id} className={classes.join(' ')}>
              <span className="active-pills__mark" aria-hidden="true">
                {pill.excluded ? '−' : filterTexts.pills.prefix}
              </span>
              {pill.group ? <span className="active-pills__group">{pill.group}:</span> : null}
              <span className="active-pills__value">{pill.value}</span>
              {pill.excluded ? (
                <span className="visually-hidden">{filterTexts.algebra.excluded}</span>
              ) : null}
              <IconButton
                className="active-pills__remove"
                label={filterTexts.pills.remove(name)}
                icon={<IconClose size={16} />}
                variant="ghost"
                size="s"
                onClick={() => setFilters(pill.next)}
              />
            </span>
          )
        })}

        {shown.length > 0 ? (
          <Button variant="ghost" size="s" onClick={state.clearAll}>
            {filterTexts.pills.clearAll}
          </Button>
        ) : null}
      </div>

      {/* §5.4: снимок разошёлся с фильтром — строка, а не исчезновение строк. */}
      {stale > 0 ? (
        <p className="active-pills__stale t-meta">
          {filterTexts.pills.stale(stale)}
          <span className="active-pills__sep" aria-hidden="true"> · </span>
          <button type="button" className="active-pills__refresh t-meta" onClick={state.refresh}>
            {filterTexts.pills.refresh}
          </button>
        </p>
      ) : null}
    </div>
  )
}
